import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Button, FormGroup } from 'reactstrap'
import { X } from 'react-feather'

import EditableRow from '~/components/EditableRow'
import { Show } from '~/components/commons'
import useFormikState from '~/hooks/useFormikState'
import PhoneInput from '~/components/PhoneInput'
import { formatPhoneNumberIntl } from 'react-phone-number-input'
import _ from 'lodash'

const AditionalPhoneInput = React.memo(({ id, value, onChange, onRemove }) => {
  return (
    <div className="d-flex mt-1">
      <div className="w-100">
        <label htmlFor={`aditionalPhone-${id}`} style={{ opacity: 0.6 }}>
          <FormattedMessage id="Additional number" />:
        </label>
        <PhoneInput
          className="form-control"
          id={`aditionalPhone-${id}`}
          name={`aditionalPhone-${id}`}
          onChange={onChange}
          value={value}
          country="BR"
        />
      </div>
      <Button
        style={{
          padding: '0',
          height: 25,
          alignSelf: 'flex-end',
          marginBottom: 5,
        }}
        color="link"
        onClick={onRemove}
        type="button"
      >
        <X size={25} color="#8D8D8D" />
      </Button>
    </div>
  )
})

function PrimaryAditionalPhoneField({
  controller,
  label,
  onSwitchChange,
  editableFields,
  ...props
}) {
  const primaryName = 'phoneNumbers.primaryPhone'
  const aditionalName = 'phoneNumbers.additionalPhones'
  const switchId = `${primaryName}Switch`

  const [primaryPhone, setPrimaryPhone] = useFormikState(
    controller,
    primaryName
  )

  const [aditionalPhones, setAditionalPhones] = useFormikState(
    controller,
    aditionalName
  )

  function handleRemoveAditionalPhone(id) {
    setAditionalPhones(aditionalPhones.filter((_, index) => index !== id))
  }

  const handleChangeAditionalPhone = (id, value) => {
    const newValues = [...aditionalPhones]
    newValues[id] = formatPhoneNumberIntl(value)
    setAditionalPhones(newValues)
  }

  return (
    <>
      <EditableRow
        switchId={switchId}
        onChange={id => {
          if (editableFields?.[switchId]) {
            const primaryPhoneInitialValues = _.get(
              controller.initialValues,
              primaryName,
              ''
            )
            const additionalPhonesInitialValues = _.get(
              controller.initialValues,
              aditionalName,
              []
            )

            setPrimaryPhone(primaryPhoneInitialValues)
            setAditionalPhones(additionalPhonesInitialValues)
          }
          onSwitchChange(id)
        }}
        checkboxStyle={{
          alignSelf: 'start',
          marginTop: '27px',
        }}
        isChecked={!!editableFields?.[switchId]}
      >
        <FormGroup>
          <label htmlFor={primaryName}>
            <FormattedMessage id={label} />:
          </label>
          <PhoneInput
            id={primaryName}
            name={primaryName}
            disabled={!editableFields?.[switchId]}
            value={primaryPhone}
            onChange={value =>
              setPrimaryPhone(
                formatPhoneNumberIntl(value) || value?.replace(/\D/g, '')
              )
            }
            country="BR"
            international="false"
            {...props}
          />
          <Show
            condition={
              aditionalPhones?.length >= 1 && editableFields?.[switchId]
            }
          >
            <AditionalPhoneInput
              value={aditionalPhones?.[0]}
              onChange={v => handleChangeAditionalPhone(0, v)}
              onRemove={() => handleRemoveAditionalPhone(0)}
            />
          </Show>
          <Show
            condition={
              aditionalPhones?.length >= 2 && editableFields?.[switchId]
            }
          >
            <AditionalPhoneInput
              value={aditionalPhones?.[1]}
              onChange={v => handleChangeAditionalPhone(1, v)}
              onRemove={() => handleRemoveAditionalPhone(1)}
            />
          </Show>
          <Show
            condition={
              !!editableFields?.[switchId] && aditionalPhones?.length < 2
            }
          >
            <Button
              onClick={() => setAditionalPhones([...aditionalPhones, ''])}
              style={{ padding: '10px 0', fontSize: 14 }}
              color="link"
              type="button"
            >
              <FormattedMessage id="Add phone number" />
            </Button>
          </Show>
        </FormGroup>
      </EditableRow>
    </>
  )
}

export default React.memo(PrimaryAditionalPhoneField)
